import React from "react";
import { useParams, Navigate } from "react-router-dom";
import SEO from "../components/SEO";
import InvestorList from "../components/InvestorList";
import { countryData } from "../data/countryData";

// 🚀 Production Imports: All 22 Custom Ecosystem Inventories mapped cleanly
import InvestorsSouthAfrica from "./InvestorsSouthAfrica";
import InvestorsNigeria from "./InvestorsNigeria";
import InvestorsKenya from "./InvestorsKenya";
import InvestorsEgypt from "./InvestorsEgypt";
import InvestorsGhana from "./InvestorsGhana";
import InvestorsMorocco from "./InvestorsMorocco";
import InvestorsAngola from "./InvestorsAngola";
import InvestorsRwanda from "./InvestorsRwanda";
import InvestorsUganda from "./InvestorsUganda";
import InvestorsTanzania from "./InvestorsTanzania";
import InvestorsSenegal from "./InvestorsSenegal";
import InvestorsZimbabwe from "./InvestorsZimbabwe";
import InvestorsNamibia from "./InvestorsNamibia";
import InvestorsBotswana from "./InvestorsBotswana";
import InvestorsMozambique from "./InvestorsMozambique";
import InvestorsMali from "./InvestorsMali";
import InvestorsLibya from "./InvestorsLibya";
import InvestorsAlgeria from "./InvestorsAlgeria";
import InvestorsBurundi from "./InvestorsBurundi";
import InvestorsLesotho from "./InvestorsLesotho";
import InvestorsDRC from "./InvestorsDRC";
import InvestorsZambia from "./InvestorsZambia";

const InvestorsCountryPage = () => {
  const { countrySlug } = useParams();

  // Normalize route string dynamically to prevent token mismatch errors
  const normalizedSlug = countrySlug ? countrySlug.toLowerCase().trim() : "";

  // Switchboard map directing Googlebot directly to the engineered master-class codebases
  const customComponentMap = {
    "south-africa": <InvestorsSouthAfrica />,
    "nigeria": <InvestorsNigeria />,
    "kenya": <InvestorsKenya />,
    "egypt": <InvestorsEgypt />,
    "ghana": <InvestorsGhana />,
    "morocco": <InvestorsMorocco />,
    "angola": <InvestorsAngola />,
    "rwanda": <InvestorsRwanda />,
    "uganda": <InvestorsUganda />,
    "tanzania": <InvestorsTanzania />,
    "senegal": <InvestorsSenegal />,
    "zimbabwe": <InvestorsZimbabwe />,
    "namibia": <InvestorsNamibia />,
    "botswana": <InvestorsBotswana />,
    "mozambique": <InvestorsMozambique />,
    "mali": <InvestorsMali />,
    "libya": <InvestorsLibya />,
    "algeria": <InvestorsAlgeria />,
    "burundi": <InvestorsBurundi />,
    "lesotho": <InvestorsLesotho />,
    "drc": <InvestorsDRC />,
    "zambia": <InvestorsZambia />
  };

  // 1. If an explicit, highly optimized custom page exists, serve it immediately to capture full SEO weights
  if (customComponentMap[normalizedSlug]) {
    return customComponentMap[normalizedSlug];
  }

  // 2. Fallback tracking logic for general or un-optimized data structures
  const data = countryData[normalizedSlug];
  if (!data) {
    return <Navigate to="/" replace />;
  }

  const mappedFaqSchema = data.extendedFAQs 
    ? data.extendedFAQs.map(item => ({
        question: item.q,
        answer: item.a
      })) 
    : [];

  // Defensive formatting logic for city localization lists
  const renderCityString = () => {
    if (!data.cities || data.cities.length === 0) return data.countryName;
    if (data.cities.length === 1) return data.cities[0];
    return `${data.cities.slice(0, -1).join(", ")}, or ${data.cities.slice(-1)}`;
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Fixed template string string interpolations with explicit relative backticks */}
      <SEO
        title={`Top Investors in ${data.countryName} 2026 | Angel Investors & Venture Capital`}
        description={`Connect with verified angel investors, venture capital firms, and seed funding in ${data.countryName}. Browse active funding networks across ${data.cities ? data.cities.join(", ") : data.countryName}.`}
        canonicalUrl={`https://investorlanes.com{normalizedSlug}`}
        image={`https://investorlanes.com{normalizedSlug}.jpg`}
        faqSchema={mappedFaqSchema}
      />

      <h1 className="text-3xl font-extrabold mb-4 text-gray-900">
        Top Investors in {data.countryName} 2026 {data.flag}
      </h1>
      
      <p className="mb-6 text-gray-700 leading-relaxed">
        Explore our curated directory of <strong>verified investors in {data.countryName}</strong>. 
        Whether your startup is based in {renderCityString()}, InvestorLanes bridges the gap between founders and active funding ecosystems.
      </p>

      <h2 className="text-2xl font-bold mb-4 text-gray-800">🌟 Featured {data.countryName} Investors</h2>
      <InvestorList investors={data.investors || []} />

      {data.extendedFAQs && data.extendedFAQs.length > 0 && (
        <>
          <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">💡 Frequently Asked Questions</h2>
          <div className="space-y-4 mb-8">
            {data.extendedFAQs.map((faq, index) => (
              <div key={index} className="bg-gray-50 p-5 rounded-lg border border-gray-100">
                <h3 className="font-bold text-gray-900 text-lg flex items-start">
                  <span className="text-blue-600 mr-2">Q:</span> {faq.q}
                </h3>
                <p className="text-gray-700 mt-2 pl-6 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-bold text-blue-900 mb-2">Next Steps for Founders:</h4>
        <p className="text-sm text-gray-700">
          Learn exactly how to prepare your pitch deck and secure capital by exploring our{" "}
          <a href={`/blog/${normalizedSlug}-funding-guide`} className="text-blue-600 font-medium underline ml-1">
            {data.countryName} Startup Funding Guide →
          </a>
        </p>
      </div>
    </div>
  );
};

export default InvestorsCountryPage;
